import { getCaptcha } from './frontStageApi';
import { authCheck } from './backStageApi';

enum Estatus {
  upload = 0,
  status,
  result
};

/**
 * Reads the provided file and checks its format.
 *
 * @param file - The file to be read.
 * @param setWrongFormat - Callback function to set the wrong format flag.
 * @param setStatus - Callback function to set the status.
 * @param setUploadFile - Callback function to set the uploaded file.
 */
export const readfile = (
  file: File,
  setWrongFormat: (wrong: boolean) => void,
  setStatus: (status: Estatus) => void,
  setUploadFile: (file: File) => void,
  setCaptcha: (captcha: string) => void
) => {
  const doubleExtension = file.name.split('.');
  const allowedExtensions = /(\.pdf)$/i;

  setWrongFormat(false);
  if (file && allowedExtensions.exec(file.name) && doubleExtension.length === 2) {
    setUploadFile(file);
    setStatus(Estatus.status);
    getCaptcha(setCaptcha);
  } else {
    setStatus(Estatus.upload);
    setWrongFormat(true);
  }
}

/**
 * Adds commas to a number for better readability.
 *
 * @param number - The number (or string representation of a number) to be formatted.
 * @returns The formatted number with commas.
 */
export const numAddComma = (number: any): string | any => {
  if (typeof number === 'string' || number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return number;
}

const parseDateOnly = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDateOnly = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * 計算機構目前所在的年度滾動生效區間（與後端 update_issuer_counts.py 的 compute_current_window 邏輯一致），
 * 回傳區間起、迄日期（迄日為區間內最後一天，含當天）；若尚未設定起始日期則回傳 null。
 */
export const getIssuerEffectivePeriod = (planStartDate: string | null): { start: string; end: string } | null => {
  if (!planStartDate) return null;

  const start = parseDateOnly(planStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let yearsElapsed = 0;
  if (start <= today) {
    yearsElapsed = today.getFullYear() - start.getFullYear();
    const probe = new Date(start);
    probe.setFullYear(start.getFullYear() + yearsElapsed);
    if (probe > today) yearsElapsed -= 1;
  }

  const windowStart = new Date(start);
  windowStart.setFullYear(start.getFullYear() + yearsElapsed);

  const windowEndExclusive = new Date(windowStart);
  windowEndExclusive.setFullYear(windowStart.getFullYear() + 1);

  const windowEndInclusive = new Date(windowEndExclusive);
  windowEndInclusive.setDate(windowEndInclusive.getDate() - 1);

  return { start: formatDateOnly(windowStart), end: formatDateOnly(windowEndInclusive) };
};

/**
 * Checks the authentication status of the user.
 * If the user is not authenticated, redirects them to the login page.
 */
export const checkAuth = async () => {
  const response = await authCheck();
  if (response && response.applied.toString() !== 'true') {
    document.location.href = (window as any).env.MAIN_HOST + '/accounts/login/';
  }
};